import { TooltipProvider } from '@flashka/shared/ui';

import * as Sentry from '@sentry/react';
import React, { Suspense, useEffect, useState } from 'react';
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';

import { enrichEvent, registerDiagnosticsSource } from './lib/sentry-diagnostics';
import {
  apiTracePropagationTargets,
  beforeSendFilter,
  IGNORED_ERRORS,
  ownBundleUrls,
  sentryEnvironment,
} from './lib/sentry-filters';
Sentry.init({
  dsn: 'https://894473f8f6b68ed272497ab691a5ed7f@o4505868160073728.ingest.us.sentry.io/4509037462487040',
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  // Matches mobile (flashka-app/src/lib/sentry.ts). A Hermes profile of the app attributed
  // ~13% of ALL JavaScript work to Sentry span construction, and `tracesSampleRate` gates only
  // what is SENT — spans are BUILT for every session regardless. 1.0 meant every web user paid
  // full browser-tracing overhead to produce traces that are overwhelmingly discarded.
  //
  // ZERO UNDER THE DEV SERVER. A SAMPLED trace's idle span re-walks its whole span tree on
  // every span that starts (`client.on("spanStart")` → `getSpanDescendants`), and again, per
  // child, when it ends — which it does the moment the tab is hidden, i.e. on EVERY navigation
  // away. Quadratic in the span count, and Vite dev serves each module as its own request, so
  // a dev page carries thousands of resource spans a production bundle never has. Measured
  // 2026-09-23 (Chrome trace of the unload, JS sampling on): one `visibilitychange` listener —
  // Sentry's — ran 51–90s inside `CommitNavigation`, 64s of it self time in `addSpanChildren`,
  // and the next page could not commit until it returned. At 0.15 that hit roughly one page
  // load in seven, which is why it read as random E2E timeouts (`page.goto … 60000ms`) and a
  // tab that "hangs on navigate" rather than as a Sentry cost. Errors still report from dev.
  tracesSampleRate: import.meta.env.DEV ? 0 : 0.15,
  // `sentry-trace` + `baggage` on every call to our own `/api`, sampled or not, so the
  // API's error events and log lines carry this page's trace id (server/observability/sentry.ts).
  tracePropagationTargets: apiTracePropagationTargets(),
  // Without these two every event read `environment: production` (the SDK default — so local
  // dev servers and local E2E builds reported as prod) and `release: VERCEL_GIT_COMMIT_SHA`
  // (the Vercel project's env var holds that literal placeholder, which the Sentry plugin
  // picked up), so every deploy was one release and "resolved in next release" meant nothing.
  // `__APP_BUILD_ID__` is the validated commit SHA (vite.config.mts `resolveBuildId`), and the
  // Sentry plugin uploads source maps under the same name.
  environment: sentryEnvironment(import.meta.env.DEV, __SENTRY_ENVIRONMENT__),
  release: __APP_BUILD_ID__,
  // Third-party scripts on the page (Featurebase widget, TikTok pixel, in-app-browser
  // injections) throw into the same window.onerror; only our bundle's frames are ours to fix.
  ...(import.meta.env.PROD ? { allowUrls: ownBundleUrls(window.location.origin) } : {}),
  ignoreErrors: IGNORED_ERRORS,
  // A request that was never answered (offline, connection lost, aborted) is expected flow,
  // not an error — whichever call site captured it. Same filter as mobile
  // (flashka-app/src/lib/sentry.ts), from `@flashka/shared/api`. Breadcrumbs are untouched.
  // Plus lazy-chunk failures the app recovers from by reloading (./lib/chunk-load-error).
  // What survives gets the context its issue would otherwise lack (./lib/sentry-diagnostics).
  beforeSend: (event, hint) => {
    const kept = beforeSendFilter(event, hint);
    return kept && enrichEvent(kept, hint);
  },
});

// Every report links to the PostHog recording of the same session.
registerDiagnosticsSource({
  posthog: () => {
    const sessionId = posthog.get_session_id();
    if (!sessionId) return null;
    return {
      sessionId,
      replayUrl: posthog.get_session_replay_url({
        withTimestamp: true,
        timestampLookBack: 30,
      }),
    };
  },
});

enableMapSet();

import { QueryClientProvider } from '@tanstack/react-query';
import { enableMapSet } from 'immer';
import posthog from 'posthog-js';

import { installAccountSwitchReset } from '@/auth/better/client';
import { AuthProvider } from '@/auth/better/session';
import { queryClient } from '@/lib/query-client';

import { loadAppShell, prefetchAppForSignIn } from './app-shell-loader';
import { PageLoader } from './components/loading/page-loader';
import { CurrencyProvider } from './contexts/currency-context';
import { ErrorBoundary } from './Error';
import { LandingShell } from './landing-shell';
import {
  fetchDeployedBuild,
  handlePreloadError,
} from './lib/auto-update/use-auto-update';
import {
  chunkUrlFromError,
  classifyChunkFailure,
  shouldReportMissingChunk,
} from './lib/chunk-load-error';
import { Metadata } from './metadata/context';
import { ABConfigProvider } from './providers/ab-config';
import { ABConfigAuthedProvider } from './providers/ab-config-authed';
import { PHProvider } from './providers/posthog';
import { ThemeDOMSync, ThemeProvider } from './settings/theme';
import { isLandingShellPath } from './shell-paths';

// Sign-out reloads the page, but a sign-in does not — the dialog signs a
// visitor in in place, and a sign-in in ANOTHER tab reaches this one on its
// next session refetch. Either way the cache still holds the last account's
// answers under keys with no user in them (FLASHKA-VITE-277: a silent PATCH
// from the previous account's cached social `/me`). Module scope, so it is in
// place before the first session value settles.
installAccountSwitchReset(queryClient);

/**
 * The signed-in app, as its own chunk (see `app-shell.tsx`). Requested at
 * once when the page opens on an app path, so it downloads while the entry
 * boots rather than after the first render.
 */
const AppShell = React.lazy(loadAppShell);
if (typeof window !== 'undefined' && !isLandingShellPath(window.location.pathname)) {
  void loadAppShell();
}

function App() {
  return (
    <>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          {/* ONE app-wide tooltip provider: Radix keeps its skip-delay state
              here, so moving from an open tooltip straight to another trigger
              opens the second one instantly instead of re-waiting the delay.
              A per-Tooltip provider (the old shape) can't share that. */}
          <TooltipProvider>
            <CurrencyProvider>
              <Routing />
            </CurrencyProvider>
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </>
  );
}

window.addEventListener('vite:preloadError', (event) => {
  const error = (event as Event & { payload?: unknown }).payload;
  console.error('A vite preloadError has occurred:', error);
  // Telemetry: this is the only signal that clients are hitting missing or
  // poisoned chunk URLs (deploy skew / cache poisoning) — without it those
  // users are invisible, since the version-poll PostHog event only fires for
  // strictly-newer deploys. It used to be a Sentry message per failure —
  // 16.7k events / 2.8k users a week (FLASHKA-VITE-1YQ), on top of the
  // boundary's own report of the same failure (FLASHKA-VITE-122, -1QJ), and
  // with no way to tell skew from a dropped download. It is a COUNT, so it
  // goes to analytics, with the chunk and which of the two it was; Sentry
  // hears about a chunk failure only when the reload did not cure it, or when
  // the deployment this page was built for no longer serves the chunk.
  const chunk = chunkUrlFromError(error);
  void classifyChunkFailure(chunk).then((kind) => {
    try {
      posthog.capture('web_chunk_load_failed', {
        chunk,
        kind,
        buildId: __APP_BUILD_ID__,
        pathname: window.location.pathname,
      });
    } catch {
      // analytics is best-effort
    }
    if (kind !== 'missing') return;
    // A 404 is skew unless the deployment serving version.json is this very
    // build — then the deploy itself is broken, and the app-root boundary may
    // never see it (a lazy import under a `fallback={null}` boundary, or in a
    // handler). Reported as a COPY: a handler's rejection reached Sentry's
    // unhandledrejection handler first, which marks the original as already
    // captured (and beforeSend dropped it), so capturing the original again
    // would be silently skipped. Same name, message and stack, so it groups
    // with the boundary's own `persistent` report.
    void fetchDeployedBuild().then((deployed) => {
      if (
        shouldReportMissingChunk(
          kind,
          chunk,
          deployed?.buildTime ?? null,
          __APP_BUILD_TIME__,
        )
      ) {
        const report =
          error instanceof Error
            ? Object.assign(new Error(error.message), {
                name: error.name,
                stack: error.stack,
              })
            : error;
        Sentry.captureException(report, {
          tags: { chunkLoad: 'persistent', chunkLoadSource: 'preload-error' },
          extra: { chunk, buildId: __APP_BUILD_ID__ },
        });
      }
    });
  });
  // Either cause is cured by a reload. Surface the update toast so the user
  // can reload when ready — we never force a reload from here (a route-level
  // failure lands in ErrorPage, which reloads on its own).
  handlePreloadError();
});

const Routing = () => {
  return (
    <BrowserRouter>
      <ThemeDOMSync />
      <Metadata>
        <AuthProvider>
          <PHProvider>
            <ABConfigProvider>
              <ABConfigAuthedProvider>
                <Shells />
              </ABConfigAuthedProvider>
            </ABConfigProvider>
          </PHProvider>
        </AuthProvider>
      </Metadata>
    </BrowserRouter>
  );
};

/**
 * Which shell renders. A visit that opens on the landing (or About) gets the
 * landing shell and never downloads the app; the first step to any other
 * path — a sign-in landing on `/onboarding`, Get Started for a signed-in
 * visitor, a link to the blog — loads the app shell, which then serves every
 * route, the landing included, for the rest of the visit. Never back: the app
 * would remount (and its sync session restart) each time.
 */
/** The wait for the app shell: the page loader (the Flashka mark, shown only
 *  after a beat so a cached chunk never flashes it) on the page's own ground.
 *  Plain SVG + CSS — the app's Lottie player would land in every landing
 *  visit's entry. */
function AppShellPending() {
  return <PageLoader variant="screen" />;
}

function Shells() {
  const { pathname } = useLocation();
  const [inApp, setInApp] = useState(() => !isLandingShellPath(pathname));
  if (!inApp && !isLandingShellPath(pathname)) setInApp(true);

  if (!inApp) return <LandingShell prefetchApp={prefetchAppForSignIn} />;
  return (
    // The boundary catches a failed app-shell chunk (a landing tab left open
    // across a deploy, then Sign up): the landing shell — and its toaster —
    // is already gone, so without it the page goes white for good. ErrorPage
    // reloads on its own for chunk-load errors.
    <ErrorBoundary>
      {/* The landing is gone by now (a footer link to the blog, Get Started
          for a signed-in visitor): the page loader, not a white page, while the
          chunk downloads. Its own routes bring their loading states. */}
      <Suspense fallback={<AppShellPending />}>
        <AppShell />
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
